import { settleMount } from "#ce/framework/mount-ready";
import "#legacy/admin/page_builder/tool.js";
/**
 * Load lifecycle:
 *  tool.setState - used by tools with more complicated state requirements. Most tools just pass the component context to their templates, so that's the default
 *  tool.render - by default updates the tool element with markup generated by tool template, overrid for react apps
 *  tool.bind - bind events to the tool or component here
 *  tool.unbind - unbind any events
 *
 * Update lifecycle
 *  tool.setState
 *  tool.update
 *  tool.rendered (if the component was rendered)
 *
 * General Notes
 *  - render/bind/unbind are only called on load, after that it's setcontext and update. update is
 *  - identical to render by default, but you can override it. the update lifecycle is only triggered if
 *  - another tool changes the splinter, so if your tool changes a context value, you update your view there.
 */

(function (Event) {
  "use strict";

  Sparkle.PageBuilder.Tool({
    /**
     * @description Unique identifier for the tool component.
     */
    name: "appearance",

    /**
     * @description The template file to use to load and render the component.
     * @see settings-editor-template.hbs
     */
    templateId: "appearance-editor-template",

    /**
     * @override
     * @name shouldLoad
     * @description
     *  Return whether or not this tool should always load. In this case,
     *   because this tool will always need to load for every tool, we want
     *   to go ahead and force the loading.
     * @return {boolean}
     */
    shouldLoad: function () {
      return true;
    },

    /**
     * @override
     * @name render
     * @description
     *  Render the tool component using the built in template renderer. This is
     *   an override of the base component render, and will use special properties
     *   not usually accessed by other tools.
     * @param {HTMLElement} element
     */
    render: function (element) {
      element = element || this.element;

      if (this.template && this.element) {
        const classes = this.component.getClasses();
        const settings = Object.entries(this.component.config.select_items || {}).map(([group, items]) => ({
          group,
          items,
          searchable: this.component.config.select_searchable[group],
          value: items.find((item) => classes.includes(item.value))?.value || items[0]?.value,
        }));
        var markup = this.template({ settings });

        element.innerHTML = markup;
      }
    },

    /**
     * @name bind
     * @description
     * Bind the different event listeners to the component, and also add in
     *  any extra functionality to manage the slideshow
     * @param {HTMLElement} component
     */
    bind: async function (component) {
      const tool = this;
      const controls = await Promise.all(Array.from(this.element.querySelectorAll("sp-select"), settleMount));

      for (const select of controls.filter(Boolean)) {
        const optionGroup = select.dataset.settingsGroup;
        const group = select.closest(".group-settings-option");
        let previousSelection = select.value;

        function saveAppearance() {
          if (select.value === undefined) {
            select.value = previousSelection;
            return;
          }

          previousSelection = select.value;

          for (const className of Object.keys(component.config.options[optionGroup])) {
            component.removeClass(className);
          }

          component.addClass(select.value);
          component.save();
        }

        select.addEventListener("change", saveAppearance);

        if (!component.shared && !component.inherited) {
          group.classList.add("unique");
          continue;
        }

        if (!component.isKeyCustomized(optionGroup)) {
          group.classList.add("shared-value");

          function customizeAppearance() {
            group.classList.remove("shared-value");
          }

          group.addEventListener("click", customizeAppearance);
        }

        function pushAppearance(event) {
          component.pushClass(select.value);
          group.classList.add("shared-value");
          event.preventDefault();
        }

        group.querySelector(".shared-component-options .setting-push")?.addEventListener("click", pushAppearance);

        function restoreAppearance(event) {
          component.restoreClass(optionGroup);
          Event.trigger("pb:tool:outdated", tool);
          group.classList.add("shared-value");
          event.preventDefault();
        }

        group.querySelector(".shared-component-options .setting-reset")?.addEventListener("click", restoreAppearance);
      }
    },
  });
})(Sparkle.PageBuilder.Helpers.Event);
